import { SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  Wrap,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { TalesSpinner } from "../../../../atoms";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import {
  getFaqData,
  getFaqError,
  isFaqLoading,
} from "../../../../../store/faq/selectors";
import { addFaq } from "../../../../../store/faq/actions/add";
import { toggleFaqVisibility } from "../../../../../store/faq/actions/toggleVisibility";
import { deleteFaq } from "../../../../../store/faq/actions/delete";
import useFaqs from "../../../../../utils/hooks/useFaqs";

interface FaqInput {
  question: string;
  answer: string;
  show: boolean;
}

export default function Faq() {
  useFaqs();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isFaqLoading);
  const faqs = useSelector(getFaqData);
  const error = useSelector(getFaqError);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<FaqInput>({
    defaultValues: {
      question: "",
      answer: "",
      show: true,
    },
  });

  const onSubmit: SubmitHandler<FaqInput> = (data) => {
    const newFaq = {
      question: getValues("question"),
      answer: getValues("answer"),
      show: getValues("show"),
    };
    dispatch(addFaq(newFaq));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {faqs &&
          faqs.map((faq) => (
            <Card key={faq.question} w="xs" m="10px">
              <CardHeader>
                <Heading size="base">{faq.question}</Heading>
              </CardHeader>
              <CardBody>
                <Text>{faq.answer}</Text>
              </CardBody>
              <CardFooter>
                <Button
                  aria-label={faq.show ? "Nascondi" : "Mostra"}
                  variant="solid"
                  colorScheme={faq.show ? "gray" : "green"}
                  onClick={() => dispatch(toggleFaqVisibility(faq))}
                  marginRight="20px"
                >
                  {faq.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
                </Button>
                <IconButton
                  aria-label="Elimina"
                  variant="solid"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={() => dispatch(deleteFaq(faq))}
                />
              </CardFooter>
            </Card>
          ))}
        <Card w="xs" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuovo</Heading>
              </CardHeader>
              <CardBody>
                <FormControl
                  marginBottom="20px"
                  isInvalid={errors.question != null}
                  isRequired={true}
                >
                  <FormLabel htmlFor="question">Domanda</FormLabel>
                  <Input
                    id="question"
                    type="text"
                    {...register("question", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.question && errors.question.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired={true}
                  isInvalid={errors.answer != null}
                  marginBottom="20px"
                >
                  <FormLabel htmlFor="answer">Risposta</FormLabel>
                  <Textarea
                    id="answer"
                    {...register("answer", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.answer && errors.answer.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    watch("answer") === "" ||
                    !watch("question") ||
                    faqs?.some((faq) => faq.question === watch("question"))
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
